import { Slider } from 'antd';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface AirlineInfo {
    code: string;
    name: string;
}

interface FlightFilterProps {
    airlines: AirlineInfo[];
    onFilterChange: (filters: any) => void;
    resultsCount?: number;
}

const FlightFilter: React.FC<FlightFilterProps> = ({
    airlines,
    onFilterChange,
    resultsCount = 0
}) => {
    console.log('Airlines prop:', airlines);

    const [searchTerm, setSearchTerm] = useState('');

    // Detect mobile screen size
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        checkMobile();

        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    const [filters, setFilters] = useState({
        priceRange: [0, 500000],
        selectedAirlines: [] as string[],
        cabinClass: [] as string[],
        stops: 'any',
        amenities: [] as string[],
        durationRange: [0, 1140],
        departureTime: [] as string[],
        arrivalTime: [] as string[],
    });

    useEffect(() => {
        onFilterChange(filters);
    }, [filters, onFilterChange]);

    const handlePriceChange = (value: number[]) => {
        setFilters(prev => ({ ...prev, priceRange: value }));
    };

    const handleDurationChange = (value: number[]) => {
        setFilters(prev => ({ ...prev, durationRange: value }));
    };

    const handleAirlineChange = (airline: string, isChecked: boolean) => {
        setFilters(prev => {
            const updatedAirlines = isChecked
                ? [...prev.selectedAirlines, airline]
                : prev.selectedAirlines.filter(a => a !== airline);

            return {
                ...prev,
                selectedAirlines: updatedAirlines
            };
        });
    };

    const handleCabinClassChange = (cabin: string, isChecked: boolean) => {
        setFilters(prev => {
            const updatedCabins = isChecked
                ? [...prev.cabinClass, cabin]
                : prev.cabinClass.filter(c => c !== cabin);

            return {
                ...prev,
                cabinClass: updatedCabins
            };
        });
    };

    const handleStopsChange = (stops: string, isChecked: boolean) => {
        setFilters(prev => ({
            ...prev,
            stops: isChecked ? stops : 'any'
        }));
    };

    const handleAmenityChange = (amenity: string, isChecked: boolean) => {
        setFilters(prev => {
            const updatedAmenities = isChecked
                ? [...prev.amenities, amenity]
                : prev.amenities.filter(a => a !== amenity);

            return {
                ...prev,
                amenities: updatedAmenities
            };
        });
    };

    const handleTimeFilterChange = (
        timeRange: string,
        type: 'departureTime' | 'arrivalTime',
        isChecked: boolean
    ) => {
        setFilters(prev => {
            const updatedTimes = isChecked
                ? [...prev[type], timeRange]
                : prev[type].filter(t => t !== timeRange);

            return {
                ...prev,
                [type]: updatedTimes
            };
        });
    };

    const resetFilters = () => {
        setFilters({
            priceRange: [0, 500000],
            selectedAirlines: [],
            cabinClass: [],
            stops: 'any',
            amenities: [],
            durationRange: [0, 1140],
            departureTime: [],
            arrivalTime: [],
        });

        setSearchTerm('');
    };

    // Filter airlines based on search term
    const filteredAirlines = airlines.filter(airline =>
        airline.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        airline.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDuration = (minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        return `${hours}h ${mins}m`;
    };

    // Time range options
    const timeRanges = [
        {
            value: 'before6am',
            label: 'Before 6 AM',
            icon: '🌙'
        },
        {
            value: '6am-12pm',
            label: '6 AM - 12 PM',
            icon: '☀️'
        },
        {
            value: '12pm-6pm',
            label: '12 PM - 6 PM',
            icon: '⛅'
        },
        {
            value: 'after6pm',
            label: 'After 6 PM',
            icon: '🌆'
        }
    ];

    // Stops options
    const stopsOptions = [
        {
            value: 'nonstop',
            label: 'Non-stop',
            description: 'Direct flight'
        },
        {
            value: 'onestop',
            label: '1 Stop',
            description: 'One connection'
        },
        {
            value: 'twoplus',
            label: '2+ Stops',
            description: 'Multiple connections'
        }
    ];

    return (
        <div className="col-xl-3 col-lg-3">
            <div className="card filter-sidebar mb-4 mb-lg-0">

                <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">Filters</h5>

                    <Link
                        to="#"
                        className="fs-14 link-primary"
                        onClick={resetFilters}
                    >
                        Reset All
                    </Link>
                </div>

                <div className="card-body p-0">

                    {resultsCount > 0 && (
                        <div className="p-3 bg-light border-bottom">
                            <small className="text-muted">
                                Showing {resultsCount} flight
                                {resultsCount !== 1 ? 's' : ''}
                            </small>
                        </div>
                    )}

                    <form>

                        {/* Price Filter */}
                        <div className="accordion-item border-bottom p-3">
                            <div className="accordion-header">
                                <div
                                    className="accordion-button p-0 collapsed"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#accordion-price"
                                >
                                    <i className="isax isax-coin me-2 text-primary"></i>
                                    Price (₹)
                                </div>
                            </div>

                            <div
                                id="accordion-price"
                                className={`accordion-collapse collapse ${!isMobile ? 'show' : ''}`}
                            >
                                <div className="accordion-body pt-3">
                                    <div className="filter-range">
                                        <Slider
                                            range
                                            min={0}
                                            max={50000}
                                            step={500}
                                            value={filters.priceRange}
                                            onChange={handlePriceChange}
                                        />
                                    </div>

                                    <div className="filter-range-amount mt-2">
                                        <p className="fs-14 mb-0">
                                            <span className="text-gray-9 fw-medium">
                                                ₹{filters.priceRange[0].toLocaleString()} - ₹{filters.priceRange[1].toLocaleString()}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Duration Filter */}
                        <div className="accordion-item border-bottom p-3">
                            <div className="accordion-header">
                                <div
                                    className="accordion-button p-0 collapsed"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#accordion-duration"
                                >
                                    <i className="isax isax-clock me-2 text-primary"></i>
                                    Duration
                                </div>
                            </div>

                            <div
                                id="accordion-duration"
                                className={`accordion-collapse collapse ${!isMobile ? 'show' : ''}`}
                            >
                                <div className="accordion-body pt-3">
                                    <div className="filter-range">
                                        <Slider
                                            range
                                            min={0}
                                            max={720}
                                            step={30}
                                            value={filters.durationRange}
                                            onChange={handleDurationChange}
                                        />
                                    </div>

                                    <div className="filter-range-amount mt-2">
                                        <p className="fs-14 mb-0">
                                            <span className="text-gray-9 fw-medium">
                                                {formatDuration(filters.durationRange[0])} - {formatDuration(filters.durationRange[1])}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stops Filter */}
                        <div className="accordion-item border-bottom p-3">
                            <div className="accordion-header">
                                <div
                                    className="accordion-button p-0 collapsed"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#accordion-stops"
                                >
                                    <i className="isax isax-route-square me-2 text-primary"></i>
                                    Number of Stops
                                </div>
                            </div>

                            <div
                                id="accordion-stops"
                                className={`accordion-collapse collapse ${!isMobile ? 'show' : ''}`}
                            >
                                <div className="accordion-body">
                                    <div className="stops-filter">

                                        {stopsOptions.map((stop) => (
                                            <div
                                                key={stop.value}
                                                className={`stop-option card mb-2 cursor-pointer ${
                                                    filters.stops === stop.value
                                                        ? 'border-primary bg-primary-soft'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleStopsChange(
                                                        stop.value,
                                                        filters.stops !== stop.value
                                                    )
                                                }
                                            >
                                                <div className="card-body py-2">
                                                    <div className="d-flex align-items-center justify-content-between">

                                                        <div>
                                                            <h6 className="mb-0 fs-14 fw-semibold">
                                                                {stop.label}
                                                            </h6>

                                                            <small className="text-muted">
                                                                {stop.description}
                                                            </small>
                                                        </div>

                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="stopsFilter"
                                                                checked={filters.stops === stop.value}
                                                                onChange={() =>
                                                                    handleStopsChange(
                                                                        stop.value,
                                                                        true
                                                                    )
                                                                }
                                                            />
                                                        </div>

                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Any Stops */}
                                        <div
                                            className={`stop-option card mb-0 cursor-pointer ${
                                                filters.stops === 'any'
                                                    ? 'border-primary bg-primary-soft'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                handleStopsChange('any', true)
                                            }
                                        >
                                            <div className="card-body py-2">
                                                <div className="d-flex align-items-center justify-content-between">

                                                    <div>
                                                        <h6 className="mb-0 fs-14 fw-semibold">
                                                            Any stops
                                                        </h6>

                                                        <small className="text-muted">
                                                            Show all flights
                                                        </small>
                                                    </div>

                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="stopsFilter"
                                                            checked={filters.stops === 'any'}
                                                            onChange={() =>
                                                                handleStopsChange(
                                                                    'any',
                                                                    true
                                                                )
                                                            }
                                                        />
                                                    </div>

                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Departure Time Filter */}
                        <div className="accordion-item border-bottom p-3">
                            <div className="accordion-header">
                                <div
                                    className="accordion-button p-0 collapsed"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#accordion-departure"
                                >
                                    <i className="isax isax-airplane me-2 text-primary"></i>
                                    Departure Time
                                </div>
                            </div>

                            <div
                                id="accordion-departure"
                                className={`accordion-collapse collapse ${!isMobile ? 'show' : ''}`}
                            >
                                <div className="accordion-body">

                                    {timeRanges.map((time) => (
                                        <div
                                            className="form-check d-flex align-items-center ps-0 mb-2"
                                            key={time.value}
                                        >
                                            <input
                                                className="form-check-input ms-0 mt-0"
                                                type="checkbox"
                                                id={`departure-${time.value}`}
                                                checked={filters.departureTime.includes(
                                                    time.value
                                                )}
                                                onChange={e =>
                                                    handleTimeFilterChange(
                                                        time.value,
                                                        'departureTime',
                                                        e.target.checked
                                                    )
                                                }
                                            />

                                            <label
                                                className="form-check-label ms-2 d-flex align-items-center"
                                                htmlFor={`departure-${time.value}`}
                                            >
                                                <span className="me-2">
                                                    {time.icon}
                                                </span>
                                                {time.label}
                                            </label>
                                        </div>
                                    ))}

                                </div>
                            </div>
                        </div>

                        {/* Arrival Time Filter */}
                        <div className="accordion-item border-bottom p-3">
                            <div className="accordion-header">
                                <div
                                    className="accordion-button p-0 collapsed"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#accordion-arrival"
                                >
                                    <i className="isax isax-airplane me-2 text-primary"></i>
                                    Arrival Time
                                </div>
                            </div>

                            <div
                                id="accordion-arrival"
                                className={`accordion-collapse collapse ${!isMobile ? 'show' : ''}`}
                            >
                                <div className="accordion-body">

                                    {timeRanges.map((time) => (
                                        <div
                                            className="form-check d-flex align-items-center ps-0 mb-2"
                                            key={time.value}
                                        >
                                            <input
                                                className="form-check-input ms-0 mt-0"
                                                type="checkbox"
                                                id={`arrival-${time.value}`}
                                                checked={filters.arrivalTime.includes(
                                                    time.value
                                                )}
                                                onChange={e =>
                                                    handleTimeFilterChange(
                                                        time.value,
                                                        'arrivalTime',
                                                        e.target.checked
                                                    )
                                                }
                                            />

                                            <label
                                                className="form-check-label ms-2 d-flex align-items-center"
                                                htmlFor={`arrival-${time.value}`}
                                            >
                                                <span className="me-2">
                                                    {time.icon}
                                                </span>
                                                {time.label}
                                            </label>
                                        </div>
                                    ))}

                                </div>
                            </div>
                        </div>

                        {/* Airlines Filter */}
                        <div className="accordion-item border-bottom p-3">
                            <div className="accordion-header">
                                <div
                                    className="accordion-button p-0 collapsed"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#accordion-airlines"
                                >
                                    <i className="isax isax-building me-2 text-primary"></i>
                                    Airlines
                                </div>
                            </div>

                            <div
                                id="accordion-airlines"
                                className={`accordion-collapse collapse ${!isMobile ? 'show' : ''}`}
                            >
                                <div className="accordion-body">

                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search airlines..."
                                            value={searchTerm}
                                            onChange={(e) =>
                                                setSearchTerm(e.target.value)
                                            }
                                        />
                                    </div>

                                    <div
                                        className="more-content"
                                        style={{
                                            maxHeight: '200px',
                                            overflowY: 'auto'
                                        }}
                                    >
                                        {filteredAirlines.map((airline) => (
                                            <div
                                                className="form-check d-flex align-items-center ps-0 mb-2"
                                                key={airline.code}
                                            >
                                                <input
                                                    className="form-check-input ms-0 mt-0"
                                                    type="checkbox"
                                                    id={`airline-${airline.code}`}
                                                    checked={filters.selectedAirlines.includes(
                                                        airline.code
                                                    )}
                                                    onChange={e =>
                                                        handleAirlineChange(
                                                            airline.code,
                                                            e.target.checked
                                                        )
                                                    }
                                                />

                                                <label
                                                    className="form-check-label ms-2 d-flex align-items-center"
                                                    htmlFor={`airline-${airline.code}`}
                                                >
                                                    <span
                                                        className="badge bg-light text-dark border me-2"
                                                        style={{
                                                            fontSize: '10px',
                                                            minWidth: '30px'
                                                        }}
                                                    >
                                                        {airline.code}
                                                    </span>

                                                    {airline.name}
                                                </label>
                                            </div>
                                        ))}

                                        {filteredAirlines.length === 0 && (
                                            <div className="text-muted fs-14">
                                                No airlines found
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Cabin Class Filter */}
                        <div className="accordion-item border-bottom p-3">
                            <div className="accordion-header">
                                <div
                                    className="accordion-button p-0 collapsed"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#accordion-cabin"
                                >
                                    <i className="isax isax-crown me-2 text-primary"></i>
                                    Cabin Class
                                </div>
                            </div>

                            <div
                                id="accordion-cabin"
                                className={`accordion-collapse collapse ${!isMobile ? 'show' : ''}`}
                            >
                                <div className="accordion-body">

                                    {[
                                        'Economy',
                                        'Premium Economy',
                                        'Business',
                                        'First'
                                    ].map((cabin) => (
                                        <div
                                            className="form-check d-flex align-items-center ps-0 mb-2"
                                            key={cabin}
                                        >
                                            <input
                                                className="form-check-input ms-0 mt-0"
                                                type="checkbox"
                                                id={`cabin-${cabin}`}
                                                checked={filters.cabinClass.includes(
                                                    cabin
                                                )}
                                                onChange={e =>
                                                    handleCabinClassChange(
                                                        cabin,
                                                        e.target.checked
                                                    )
                                                }
                                            />

                                            <label
                                                className="form-check-label ms-2"
                                                htmlFor={`cabin-${cabin}`}
                                            >
                                                {cabin}
                                            </label>
                                        </div>
                                    ))}

                                </div>
                            </div>
                        </div>

                        {/* Amenities Filter */}
                        <div className="accordion-item border-bottom p-3">
                            <div className="accordion-header">
                                <div
                                    className="accordion-button p-0 collapsed"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#accordion-amenities"
                                >
                                    <i className="isax isax-like-shapes me-2 text-primary"></i>
                                    Amenities
                                </div>
                            </div>

                            <div
                                id="accordion-amenities"
                                className={`accordion-collapse collapse ${!isMobile ? 'show' : ''}`}
                            >
                                <div className="accordion-body">

                                    {[
                                        'Refundable',
                                        'Meal Included',
                                        'Extra Legroom',
                                        'Wi-Fi'
                                    ].map((amenity) => (
                                        <div
                                            className="form-check d-flex align-items-center ps-0 mb-2"
                                            key={amenity}
                                        >
                                            <input
                                                className="form-check-input ms-0 mt-0"
                                                type="checkbox"
                                                id={`amenity-${amenity}`}
                                                checked={filters.amenities.includes(
                                                    amenity
                                                )}
                                                onChange={e =>
                                                    handleAmenityChange(
                                                        amenity,
                                                        e.target.checked
                                                    )
                                                }
                                            />

                                            <label
                                                className="form-check-label ms-2"
                                                htmlFor={`amenity-${amenity}`}
                                            >
                                                {amenity}
                                            </label>
                                        </div>
                                    ))}

                                </div>
                            </div>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default FlightFilter;

